import React, { useState } from 'react';
import { Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import '../App.css';

const AgentTypeOptions = ['openai', 'gemini', 'retell'];

const save_template = async (id, fields, token, set_loading, set_error, set_success, navigate) => {
	set_loading(true);
	set_error('');
	set_success('');
	try {
		const headers = token ? { Authorization: 'Bearer ' + token } : {};
		if(id === 'new') {
			await axios.post(Constants.SERVER_URL + '/api/call/templates', fields, { headers });
			navigate('/agents');
		} else {
			await axios.put(Constants.SERVER_URL + '/api/call/templates/' + id, fields, { headers });
			set_success('Agent saved.');
		}
	} catch (error) {
		set_error('Failed to save agent.');
	} finally {
		set_loading(false);
	}
};

const delete_template = async (id, token, set_loading, set_error, navigate) => {
	set_loading(true);
	set_error('');
	try {
		const headers = token ? { Authorization: 'Bearer ' + token } : {};
		await axios.delete(Constants.SERVER_URL + '/api/call/templates/' + id, { headers });
		navigate('/agents');
	} catch (error) {
		set_error('Failed to delete agent.');
		set_loading(false);
	}
};

const render_spinner = () => {
	return <Spinner animation="border" size="sm" className="ms-2" />;
};

const render_alerts = (error_message, success_message) => {
	return (
		<React.Fragment>
			{error_message ? <Alert variant="danger" className="mt-3">{error_message}</Alert> : null}
			{success_message ? <Alert variant="success" className="mt-3">{success_message}</Alert> : null}
		</React.Fragment>
	);
};

const render_form = (id, fields, set_fields, loading, token, navigate, set_loading, set_error, set_success) => {
	return (
		<Form onSubmit={(event) => { event.preventDefault(); save_template(id, fields, token, set_loading, set_error, set_success, navigate); }}>
			<Form.Group className="mb-3">
				<Form.Label className="form-label">Name</Form.Label>
				<Form.Control
					type="text"
					value={fields.name}
					onChange={(event) => set_fields({ ...fields, name: event.target.value })}
					placeholder="Agent name"
					required
				/>
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Label className="form-label">Agent Type</Form.Label>
				<Form.Select
					value={fields.agent_type}
					onChange={(event) => set_fields({ ...fields, agent_type: event.target.value })}>
					{AgentTypeOptions.map(option => (
						<option key={option} value={option}>{option}</option>
					))}
				</Form.Select>
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Label className="form-label">Model</Form.Label>
				<Form.Control
					type="text"
					value={fields.model}
					onChange={(event) => set_fields({ ...fields, model: event.target.value })}
					placeholder="e.g. gpt-4o-mini"
				/>
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Label className="form-label">Temperature</Form.Label>
				<Form.Control
					type="number"
					step="0.1"
					min="0"
					max="2"
					value={fields.temperature}
					onChange={(event) => set_fields({ ...fields, temperature: event.target.value })}
					placeholder="e.g. 0.7"
				/>
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Label className="form-label">System Prompt</Form.Label>
				<Form.Control
					as="textarea"
					rows={10}
					value={fields.system_prompt}
					onChange={(event) => set_fields({ ...fields, system_prompt: event.target.value })}
					placeholder="Enter the agent system prompt..."
					required
				/>
			</Form.Group>

			<div className="agent-view-actions">
				<Button variant="primary" type="submit" disabled={loading}>
					{id === 'new' ? 'Create Agent' : 'Save Changes'}
					{loading ? render_spinner() : null}
				</Button>
				<Button
					variant="outline-secondary"
					className="ms-2"
					onClick={() => navigate('/agents')}
					disabled={loading}>
					Cancel
				</Button>
			</div>
		</Form>
	);
};

const render_delete_section = (id, confirm_delete, set_confirm_delete, token, set_loading, set_error, navigate) => {
	if(!confirm_delete) {
		return (
			<div className="agent-view-delete">
				<Button
					variant="outline-danger"
					onClick={() => set_confirm_delete(true)}>
					Delete Agent
				</Button>
			</div>
		);
	}
	return (
		<div className="agent-view-delete">
			<p className="agent-view-delete-confirm">Are you sure? This will permanently delete the agent and all of its call sessions. This cannot be undone.</p>
			<Button
				variant="danger"
				className="me-2"
				onClick={() => delete_template(id, token, set_loading, set_error, navigate)}>
				Yes, Delete
			</Button>
			<Button
				variant="outline-secondary"
				onClick={() => set_confirm_delete(false)}>
				Cancel
			</Button>
		</div>
	);
};

function AgentView() {
	const { token } = useAuth();
	const navigate = useNavigate();
	const { id } = useParams();
	const location = useLocation();

	const template = location.state ? location.state.template : null;
	const is_new = id === 'new';

	const [fields, set_fields] = useState({
		name: template ? template.name : 'Mike',
		system_prompt: template ? template.system_prompt : '',
		// model: template ? template.model : 'gemini-2.5-flash',
		model: template ? template.model : 'gemini-2.5-flash-lite',
		temperature: template ? (template.temperature !== null ? template.temperature : 0.2) : 0.2,
		agent_type: template ? template.agent_type : 'gemini',
	});

	const [loading, set_loading] = useState(false);
	const [error, set_error] = useState('');
	const [success, set_success] = useState('');
	const [confirm_delete, set_confirm_delete] = useState(false);

	if(!is_new && !template) {
		return (
			<React.Fragment>
				<main className="agents-main">
					<Alert variant="warning">
						Agent not found.
						<Button variant="link" onClick={() => navigate('/agents')}>Back to Agents</Button>
					</Alert>
				</main>
			</React.Fragment>
		);
	}

	return (
		<React.Fragment>
			<main className="agents-main">
				<div className="agents-header">
					<h2>{is_new ? 'New Agent' : 'Edit Agent'}</h2>
					<Button
						variant="outline-secondary"
						size="sm"
						onClick={() => navigate('/agents')}>
						Back
					</Button>
				</div>
				<Card className="agent-view-card">
					<Card.Body>
						{render_form(id, fields, set_fields, loading, token, navigate, set_loading, set_error, set_success)}
						{render_alerts(error, success)}
					</Card.Body>
				</Card>
				{!is_new ? render_delete_section(id, confirm_delete, set_confirm_delete, token, set_loading, set_error, navigate) : null}
			</main>
		</React.Fragment>
	);
}

export default AgentView;
